"use client";

import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  ArrowRight,
  BookOpenText,
  Copy,
  Database,
  FileUp,
  Link2,
  Play,
  Radar,
  Sparkles,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/i18n/navigation";
import { trackEvent } from "@/lib/analytics";
import { useAppContext } from "@/contexts/app";
import {
  mirofishBrandCopy,
  mirofishFaq,
  mirofishForecastExplorerTabs,
  mirofishHomeScenarioCards,
  mirofishHomeStats,
  mirofishPromptRecipes,
  mirofishScreenshots,
  mirofishTrustPillars,
  mirofishVideos,
  mirofishWorkflow,
} from "@/content/mirofish";
import { isAuthEnabled } from "@/lib/auth";
import {
  trackMiroFishHistoryOpen,
  trackMiroFishProjectCreateFailure,
  trackMiroFishProjectCreateRetry,
  trackMiroFishProjectCreateStart,
  trackMiroFishProjectCreateSuccess,
  trackMiroFishSourceDocumentRemoved,
  trackMiroFishSourceDocumentsSelected,
  trackMiroFishStartEngineGate,
} from "@/lib/mirofish-analytics";
import {
  createMiroFishClientSubmissionId,
  createMiroFishProjectFromFiles,
  createMiroFishProjectFromUrl,
  getMiroFishHistory,
} from "@/lib/mirofish-api";
import type { MiroFishProjectSummary } from "@/types/mirofish";
import type { Post } from "@/types/post";
import MiroFishHistoryDatabase from "./history-database";
import MiroFishSiteFooter from "./site-footer";
import MiroFishSiteHeader from "./site-header";

const defaultPrompt =
  "Forecast how the uploaded material evolves once agents begin reacting across public platforms, memory layers, and narrative pressure surfaces.";

const maxSourceDocumentBytes = 50 * 1024 * 1024;

type SourceMode = "upload" | "link";

const validateSourceUrl = (value: string) => {
  try {
    return new URL(value.trim()).protocol === "https:";
  } catch {
    return false;
  }
};

const getSourceHostname = (value: string) => {
  try {
    return new URL(value.trim()).hostname;
  } catch {
    return "invalid";
  }
};

const fileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

const formatDate = (value?: string) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const estimateReadTime = (content?: string) => {
  const words = (content || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/[#>*_[\]-]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  return Math.max(1, Math.ceil(words / 220));
};

const formatTagLabel = (tag: string) =>
  tag
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const isProjectCreateNetworkInterruption = (message: string) =>
  /network interrupted while creating the project/i.test(message);

const sameUploadedDocuments = (
  sourceDocuments: MiroFishProjectSummary["sourceDocuments"],
  uploadedFiles: File[]
) => {
  if (sourceDocuments.length !== uploadedFiles.length) {
    return false;
  }

  const sourceSignatures = sourceDocuments
    .map((document) => `${document.filename}:${document.size}`)
    .sort();
  const uploadedSignatures = uploadedFiles
    .map((file) => `${file.name}:${file.size}`)
    .sort();

  return sourceSignatures.every(
    (signature, index) => signature === uploadedSignatures[index]
  );
};

const findRecoveredProject = (
  items: MiroFishProjectSummary[],
  prompt: string,
  uploadedFiles: File[]
) =>
  items.find(
    (item) =>
      item.prompt.trim() === prompt.trim() &&
      sameUploadedDocuments(item.sourceDocuments, uploadedFiles)
  );

export default function MiroFishHomePage({
  featuredPosts,
}: {
  featuredPosts: Post[];
}) {
  const router = useRouter();
  const { user, setShowSignModal, setUser } = useAppContext();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [files, setFiles] = useState<File[]>([]);
  const [sourceMode, setSourceMode] = useState<SourceMode>("upload");
  const [sourceUrl, setSourceUrl] = useState("");
  const [projects, setProjects] = useState<MiroFishProjectSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchProgress, setLaunchProgress] = useState(0);
  const [launchStepLabel, setLaunchStepLabel] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [selectedExplorerTab, setSelectedExplorerTab] = useState<
    (typeof mirofishForecastExplorerTabs)[number]["key"]
  >("report");
  const authEnabled = isAuthEnabled();
  const isSignedIn = authEnabled ? Boolean(user?.uuid || user?.email) : true;

  useEffect(() => {
    const load = async () => {
      setHistoryLoading(true);
      try {
        const response = await getMiroFishHistory();
        setProjects(response.projects);
        setError(null);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Failed to load history.");
      } finally {
        setHistoryLoading(false);
      }
    };

    void load();
  }, []);

  const hasValidSource =
    sourceMode === "upload"
      ? files.length > 0 && !fileError
      : validateSourceUrl(sourceUrl) && !linkError;
  const canSubmit =
    hasValidSource && prompt.trim().length > 0 && !isPending && !isLaunching;
  const requiresFilledInputs =
    !authEnabled || (isSignedIn && user?.hasActiveSubscription === true);
  const isConsoleButtonDisabled =
    isCheckingAccess || (requiresFilledInputs ? !canSubmit : false);
  const startEngineLabel =
    authEnabled && !isSignedIn
      ? "Sign in to start engine"
      : isCheckingAccess
        ? "Checking access..."
        : isPending || isLaunching
          ? `Launching... ${Math.max(5, Math.round(launchProgress))}%`
          : "Start Engine";

  useEffect(() => {
    if (!isPending && !isLaunching) {
      return;
    }

    setLaunchProgress(8);
    setLaunchStepLabel(
      sourceMode === "link"
        ? "Downloading linked document..."
        : "Preparing source documents..."
    );

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setLaunchProgress((current) => {
        const target = Math.min(92, 8 + elapsed / 180);
        return target > current ? target : current;
      });

      if (elapsed > 4500) {
        setLaunchStepLabel("Creating project...");
      }
      if (elapsed > 10000) {
        setLaunchStepLabel("Opening workbench...");
      }
    }, 220);

    return () => {
      window.clearInterval(timer);
    };
  }, [isPending, isLaunching, sourceMode]);

  const fileSummary = useMemo(
    () => files.map((file) => `${file.name} (${Math.ceil(file.size / 1024)} KB)`),
    [files]
  );
  const fileSummaryKey = fileSummary.join("|");
  const sourceDocumentsSizeBytes = useMemo(
    () => files.reduce((sum, file) => sum + file.size, 0),
    [files]
  );
  const sourceDocumentsSizeMb = Math.ceil(sourceDocumentsSizeBytes / 1024 / 1024);

  const appendSourceFiles = (
    selectedFiles: File[],
    method: "file_picker" | "drag_drop" = "file_picker"
  ) => {
    if (!selectedFiles.length) {
      return;
    }

    const nextFiles = [...files];
    const seen = new Set(nextFiles.map(fileKey));
    let addedCount = 0;
    for (const file of selectedFiles) {
      if (seen.has(fileKey(file))) {
        continue;
      }
      nextFiles.push(file);
      seen.add(fileKey(file));
      addedCount += 1;
    }

    setFiles(nextFiles);
    if (addedCount > 0) {
      trackMiroFishSourceDocumentsSelected({
        files: nextFiles,
        addedCount,
        method,
      });
    }
    const nextSize = nextFiles.reduce((sum, file) => sum + file.size, 0);
    setFileError(
      nextSize > maxSourceDocumentBytes
        ? "Upload at most 50MB of source documents per simulation."
        : null
    );
  };

  const removeSourceFile = (targetIndex: number) => {
    setFileError(null);
    setFiles((currentFiles) => {
      const remainingFiles = currentFiles.filter((_, index) => index !== targetIndex);
      trackMiroFishSourceDocumentRemoved({ remainingFiles });
      return remainingFiles;
    });
  };

  const activeExplorerTab =
    mirofishForecastExplorerTabs.find((tab) => tab.key === selectedExplorerTab) ??
    mirofishForecastExplorerTabs[0];
  const userIdentityKey = `${user?.uuid || ""}:${user?.email || ""}`;
  const previousLaunchInputKeyRef = useRef("");
  const linkSubmissionRef = useRef<{ key: string; id: string } | null>(null);

  useEffect(() => {
    const launchInputKey = `${userIdentityKey}\n${sourceMode}\n${prompt}\n${
      sourceMode === "upload" ? fileSummaryKey : sourceUrl
    }`;
    if (
      previousLaunchInputKeyRef.current &&
      previousLaunchInputKeyRef.current !== launchInputKey &&
      !isLaunching &&
      !isPending
    ) {
      setError(null);
    }

    previousLaunchInputKeyRef.current = launchInputKey;
  }, [
    fileSummaryKey,
    isLaunching,
    isPending,
    prompt,
    sourceMode,
    sourceUrl,
    userIdentityKey,
  ]);

  const switchSourceMode = (nextMode: SourceMode) => {
    setSourceMode(nextMode);
    setError(null);
    setFileError(
      nextMode === "upload" && sourceDocumentsSizeBytes > maxSourceDocumentBytes
        ? "Upload at most 50MB of source documents per simulation."
        : null
    );
    setLinkError(
      nextMode === "link" && sourceUrl.trim() && !validateSourceUrl(sourceUrl)
        ? "Enter a valid public HTTPS link."
        : null
    );
    setLaunchProgress(0);
    setLaunchStepLabel(null);
  };

  const jumpToConsole = () => {
    document.getElementById("console")?.scrollIntoView({ behavior: "smooth" });
  };

  const applyPromptTemplate = (template: string, source: string) => {
    setPrompt(template);
    trackEvent("prompt_recipe_insert", { ui_source: source });
    jumpToConsole();
  };

  const redirectToPricing = () => {
    router.push(
      `/pricing?intent=start-engine&return_to=${encodeURIComponent("/#console")}`
    );
  };

  const resolveStartEngineAccess = async (
    source: "hero_cta" | "engine_console"
  ) => {
    if (!authEnabled) {
      trackMiroFishStartEngineGate({ source, result: "allowed" });
      return "allowed" as const;
    }

    if (!isSignedIn) {
      setShowSignModal(true);
      trackMiroFishStartEngineGate({ source, result: "sign_in" });
      return "sign_in" as const;
    }

    if (user?.hasActiveSubscription === true) {
      trackMiroFishStartEngineGate({ source, result: "allowed" });
      return "allowed" as const;
    }

    if (user?.hasActiveSubscription === false) {
      redirectToPricing();
      trackMiroFishStartEngineGate({ source, result: "pricing" });
      return "pricing" as const;
    }

    setIsCheckingAccess(true);
    try {
      const response = await fetch("/api/get-user-info", { method: "POST" });
      const payload = await response.json().catch(() => null);

      if (response.status === 401 || payload?.code === -2) {
        setShowSignModal(true);
        trackMiroFishStartEngineGate({ source, result: "sign_in" });
        return "sign_in" as const;
      }

      if (!response.ok || payload?.code !== 0 || !payload?.data) {
        setError(
          typeof payload?.message === "string" && payload.message.trim()
            ? payload.message
            : "Unable to verify your subscription. Please try again."
        );
        trackMiroFishStartEngineGate({ source, result: "error" });
        return "error" as const;
      }

      setUser(payload.data);
      if (payload.data.hasActiveSubscription === true) {
        trackMiroFishStartEngineGate({ source, result: "allowed" });
        return "allowed" as const;
      }

      redirectToPricing();
      trackMiroFishStartEngineGate({ source, result: "pricing" });
      return "pricing" as const;
    } catch {
      setError("Unable to verify your subscription. Please try again.");
      trackMiroFishStartEngineGate({ source, result: "error" });
      return "error" as const;
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const handleHeroStartEngine = async () => {
    if (isCheckingAccess) {
      return;
    }

    setError(null);
    const access = await resolveStartEngineAccess("hero_cta");
    if (access === "allowed") {
      jumpToConsole();
    }
  };

  const handleSubmit = async () => {
    if (isCheckingAccess || isPending || isLaunching) {
      return;
    }

    setError(null);
    const access = await resolveStartEngineAccess("engine_console");
    if (access !== "allowed" || !canSubmit) {
      return;
    }

    startTransition(async () => {
      setError(null);
      setIsLaunching(true);
      setLaunchProgress(10);
      setLaunchStepLabel(
        sourceMode === "link"
          ? "Downloading linked document..."
          : "Preparing source documents..."
      );
      const trackingInput = {
        files: sourceMode === "upload" ? files : [],
        prompt,
        sourceMode,
        sourceHost:
          sourceMode === "link" ? getSourceHostname(sourceUrl) : undefined,
      };
      trackMiroFishProjectCreateStart(trackingInput);
      try {
        const options = {
          onProgress: ({ progress, label }: { progress: number; label: string }) => {
            setLaunchProgress((current) => Math.max(current, progress));
            setLaunchStepLabel(label);
          },
          onRetry: ({
            retry,
            maxRetries,
          }: {
            retry: number;
            maxRetries: number;
          }) => {
            setLaunchProgress((current) => Math.max(current, 18 + retry * 8));
            setLaunchStepLabel(`Reconnecting... retry ${retry}/${maxRetries}`);
            trackMiroFishProjectCreateRetry({ retry, maxRetries });
          },
        };
        const response =
          sourceMode === "link"
            ? await createMiroFishProjectFromUrl({
                prompt,
                url: sourceUrl.trim(),
                clientSubmissionId: (() => {
                  const key = `${prompt.trim()}\n${sourceUrl.trim()}`;
                  if (linkSubmissionRef.current?.key !== key) {
                    linkSubmissionRef.current = {
                      key,
                      id: createMiroFishClientSubmissionId(),
                    };
                  }
                  return linkSubmissionRef.current.id;
                })(),
                options,
              })
            : await createMiroFishProjectFromFiles({
                prompt,
                files,
                options,
              });
        setLaunchProgress(100);
        setLaunchStepLabel("Project ready, redirecting...");
        linkSubmissionRef.current = null;
        trackMiroFishProjectCreateSuccess(trackingInput);
        router.push(`/process/${response.project.projectId}`);
      } catch (submitError) {
        const message = submitError instanceof Error
          ? submitError.message
          : "Failed to create project.";
        if (message.toLowerCase().includes("insufficient credits")) {
          trackMiroFishProjectCreateFailure({
            ...trackingInput,
            error: submitError,
          });
          setLaunchProgress(0);
          setLaunchStepLabel(null);
          redirectToPricing();
          return;
        }

        if (
          sourceMode === "upload" &&
          isProjectCreateNetworkInterruption(message)
        ) {
          setLaunchProgress(94);
          setLaunchStepLabel("Checking whether the project was created...");
          try {
            const historyResponse = await getMiroFishHistory();
            setProjects(historyResponse.projects);
            const recoveredProject = findRecoveredProject(
              historyResponse.projects,
              prompt,
              files
            );

            if (recoveredProject) {
              setLaunchProgress(100);
              setLaunchStepLabel("Project found, redirecting...");
              trackMiroFishProjectCreateSuccess({
                files,
                prompt,
                sourceMode,
                recovered: true,
              });
              router.push(`/process/${recoveredProject.projectId}`);
              return;
            }
          } catch {
            // Fall through to the original network interruption message.
          }
        }

        setLaunchProgress(0);
        setLaunchStepLabel(null);
        trackMiroFishProjectCreateFailure({
          ...trackingInput,
          error: submitError,
        });
        setError(message);
      } finally {
        setIsLaunching(false);
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#04050a] text-white">
      <MiroFishSiteHeader />

      <main>
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-[10%] top-0 h-[420px] w-[420px] rounded-full bg-sky-500/20 blur-[120px]" />
            <div className="absolute right-[8%] top-[8%] h-[420px] w-[420px] rounded-full bg-orange-500/16 blur-[120px]" />
            <div className="absolute bottom-[-12%] left-1/2 h-[460px] w-[460px] -translate-x-1/2 rounded-full bg-violet-500/12 blur-[140px]" />
          </div>

          <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-14 pt-12 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:px-8 lg:pb-18 lg:pt-18">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/6 px-4 py-2 text-xs uppercase tracking-[0.24em] text-white/65">
                <Sparkles className="h-4 w-4 text-amber-300" />
                {mirofishBrandCopy.eyebrow}
              </div>
              <h1 className="mt-6 max-w-4xl text-balance text-5xl font-semibold leading-[0.95] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
                {mirofishBrandCopy.title}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-white/72">
                {mirofishBrandCopy.description}
              </p>
              <p className="mt-4 max-w-2xl text-base leading-7 text-white/52">
                {mirofishBrandCopy.subdescription}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  type="button"
                  className="bg-white text-black hover:bg-white/90"
                  disabled={isCheckingAccess}
                  onClick={handleHeroStartEngine}
                  data-testid="mirofish-hero-start-engine"
                >
                  <Radar className="mr-2 h-4 w-4" />
                  {authEnabled && !isSignedIn ? "Sign in to start engine" : startEngineLabel}
                </Button>
                <Button
                  asChild
                  variant="ghost"
                  className="border border-white/10 bg-white/5 text-white hover:bg-white/10"
                >
                  <a href={mirofishVideos[0].href} target="_blank" rel="noreferrer">
                    <Play className="mr-2 h-4 w-4" />
                    {mirofishBrandCopy.secondaryCta}
                  </a>
                </Button>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {mirofishHomeStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-[28px] border border-white/10 bg-white/5 p-5"
                  >
                    <div className="text-2xl font-semibold text-white">{stat.value}</div>
                    <div className="mt-2 text-sm text-white/58">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative z-10 flex flex-col gap-4">
              <div className="overflow-hidden rounded-[36px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-5 shadow-[0_20px_120px_rgba(0,0,0,0.35)]">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-white/40">
                      Hero Surface
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-white">MiroFish</div>
                  </div>
                  <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.22em] text-white/55">
                    v0.1 preview
                  </div>
                </div>

                <div className="mt-5 overflow-hidden rounded-[28px] border border-white/10 bg-black/50">
                  <Image
                    src="/images/mirofish/logo-reference-left.jpg"
                    alt="MiroFish visual"
                    width={900}
                    height={700}
                    className="h-auto w-full object-cover"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="workflow" className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center gap-3 text-sm uppercase tracking-[0.26em] text-white/40">
            <Database className="h-4 w-4" />
            Workflow Sequence
          </div>
          <div className="grid gap-4 lg:grid-cols-5">
            {mirofishWorkflow.map((item) => (
              <div
                key={item.step}
                className="rounded-[28px] border border-white/10 bg-white/5 p-5"
              >
                <div className="text-xs uppercase tracking-[0.24em] text-sky-300">
                  Step {item.step}
                </div>
                <h2 className="mt-3 text-xl font-semibold text-white">{item.title}</h2>
                <p className="mt-3 text-sm leading-6 text-white/62">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="console" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,0.92fr)_minmax(400px,1.08fr)]">
            <div className="rounded-[32px] border border-white/10 bg-white/5 p-6">
              <div className="text-xs uppercase tracking-[0.26em] text-white/40">System Status</div>
              <h2 className="mt-4 text-3xl font-semibold text-white">Engine Standing By</h2>
              <p className="mt-4 max-w-lg text-sm leading-7 text-white/63">
                Upload narrative seeds, analytical reports, policy drafts, or story fragments.
                The graph, simulation, report, and interaction chain will be opened from one console.
              </p>
              <div className="mt-8 rounded-[28px] border border-white/10 bg-black/35 p-5">
                <div className="text-xs uppercase tracking-[0.24em] text-white/42">Sequence</div>
                <div className="mt-4 space-y-3">
                  {mirofishWorkflow.map((item) => (
                    <div key={item.step} className="flex gap-4">
                      <div className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/70">
                        {item.step}
                      </div>
                      <div>
                        <div className="font-medium text-white">{item.title}</div>
                        <div className="text-sm leading-6 text-white/52">{item.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.24em] text-white/42">Engine Console</div>
                  <div className="mt-2 text-2xl font-semibold text-white">Seed the simulation</div>
                </div>
                <div className="rounded-full border border-white/10 bg-black/25 px-3 py-1 text-xs uppercase tracking-[0.2em] text-white/50">
                  PDF / MD / TXT
                </div>
              </div>

              <div className="mt-6 space-y-6">
                <div>
                  <div className="mb-3 text-xs uppercase tracking-[0.22em] text-white/45">
                    01 / Reality Seeds
                  </div>
                  <Tabs
                    value={sourceMode}
                    onValueChange={(value) => switchSourceMode(value as SourceMode)}
                    className="gap-4"
                  >
                    <TabsList className="grid h-11 w-full grid-cols-2 rounded-lg border border-white/10 bg-black/30 p-1 text-white/55">
                      <TabsTrigger
                        value="upload"
                        className="rounded-md text-sm text-white/55 data-[state=active]:bg-white/12 data-[state=active]:text-white"
                        data-testid="mirofish-source-upload-tab"
                      >
                        <FileUp className="h-4 w-4" />
                        Upload File
                      </TabsTrigger>
                      <TabsTrigger
                        value="link"
                        className="rounded-md text-sm text-white/55 data-[state=active]:bg-white/12 data-[state=active]:text-white"
                        data-testid="mirofish-source-link-tab"
                      >
                        <Link2 className="h-4 w-4" />
                        Import Link
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="upload" className="mt-0">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          appendSourceFiles(
                            Array.from(event.dataTransfer.files || []),
                            "drag_drop"
                          );
                        }}
                        className="flex w-full flex-col items-center justify-center rounded-[28px] border border-dashed border-white/15 bg-black/30 px-6 py-10 text-center transition hover:border-white/30 hover:bg-black/35"
                      >
                        <FileUp className="h-8 w-8 text-sky-300" />
                        <div className="mt-4 text-base font-medium text-white">
                          Drag files here or click to browse
                        </div>
                        <div className="mt-2 max-w-sm text-sm leading-6 text-white/55">
                          PDF, MD, Markdown, or TXT, up to 50MB total.
                        </div>
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        data-testid="mirofish-file-input"
                        multiple
                        className="hidden"
                        accept=".pdf,.md,.txt,.markdown"
                        onChange={(event) => {
                          appendSourceFiles(
                            Array.from(event.target.files || []),
                            "file_picker"
                          );
                          event.currentTarget.value = "";
                        }}
                      />
                      {fileSummary.length ? (
                        <div className="mt-4 space-y-2 text-sm text-white/65">
                          <div className="flex items-center justify-between text-xs uppercase tracking-[0.16em] text-white/38">
                            <span>Selected files</span>
                            <span>{sourceDocumentsSizeMb}MB / 50MB</span>
                          </div>
                          {files.map((file, index) => {
                            const label = `${file.name} (${Math.ceil(file.size / 1024)} KB)`;
                            return (
                              <div
                                key={fileKey(file)}
                                className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                              >
                                <span className="min-w-0 flex-1 truncate">{label}</span>
                                <button
                                  type="button"
                                  aria-label={`Remove ${file.name || "source document"}`}
                                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 text-white/50 transition hover:border-white/25 hover:text-white"
                                  onClick={() => removeSourceFile(index)}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </TabsContent>

                    <TabsContent value="link" className="mt-0">
                      <div className="rounded-[28px] border border-white/10 bg-black/30 p-5">
                        <label
                          htmlFor="mirofish-source-url"
                          className="text-sm font-medium text-white"
                        >
                          Public file link
                        </label>
                        <div className="relative mt-3">
                          <Link2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                          <Input
                            id="mirofish-source-url"
                            type="url"
                            inputMode="url"
                            value={sourceUrl}
                            onChange={(event) => {
                              setSourceUrl(event.target.value);
                              setLinkError(null);
                            }}
                            onBlur={() => {
                              if (sourceUrl.trim() && !validateSourceUrl(sourceUrl)) {
                                setLinkError("Enter a valid public HTTPS link.");
                              }
                            }}
                            placeholder="https://drive.google.com/... or any public HTTPS file"
                            aria-invalid={Boolean(linkError)}
                            data-testid="mirofish-source-url"
                            className="h-12 rounded-lg border-white/10 bg-white/5 pl-11 text-white placeholder:text-white/30"
                          />
                        </div>
                        <div className="mt-3 text-sm leading-6 text-white/50">
                          Google Drive, Dropbox, OneDrive, or a direct PDF, MD, Markdown,
                          or TXT link. Maximum 10MB.
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                <div>
                  <div className="mb-3 text-xs uppercase tracking-[0.22em] text-white/45">
                    02 / Simulation Prompt
                  </div>
                  <Textarea
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    className="min-h-40 rounded-[28px] border-white/10 bg-black/35 px-5 py-4 text-base leading-7 text-white placeholder:text-white/30"
                    placeholder="Describe what should be simulated and what the report should answer."
                  />
                </div>

                {fileError || linkError || error ? (
                  <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                    <div>{fileError || linkError || error}</div>
                    {sourceMode === "upload" && error ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => switchSourceMode("link")}
                        className="mt-3 border border-red-200/20 bg-white/5 text-red-50 hover:bg-white/10 hover:text-white"
                        data-testid="mirofish-import-link-fallback"
                      >
                        <Link2 className="mr-2 h-4 w-4" />
                        Import from link
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                {isPending ? (
                  <div className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
                    <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.16em] text-white/55">
                      <span>{launchStepLabel || "Launching..."}</span>
                      <span>{Math.max(5, Math.round(launchProgress))}%</span>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                      <div
                        className="h-full rounded-full bg-sky-300/90 transition-[width] duration-300"
                        style={{ width: `${Math.max(5, Math.min(100, launchProgress))}%` }}
                      />
                    </div>
                  </div>
                ) : null}

                <Button
                  type="button"
                  disabled={isConsoleButtonDisabled}
                  onClick={handleSubmit}
                  className="h-14 w-full rounded-full bg-white text-base font-medium text-black hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/20 disabled:text-white/45"
                  data-testid="mirofish-start-engine"
                >
                  {startEngineLabel}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {!historyLoading && projects.length === 0 && !error ? null : (
          <section id="history" className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="text-xs uppercase tracking-[0.26em] text-white/40">
                History Database
              </div>
              <h2 className="mt-3 text-3xl font-semibold text-white">Simulation history</h2>
            </div>
            <MiroFishHistoryDatabase
              items={projects}
              loading={historyLoading}
              onSelect={(item) => {
                trackMiroFishHistoryOpen(item);
                router.push(`/process/${item.projectId}`);
              }}
            />
          </section>
        )}

        <section id="screenshots" className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <div className="rounded-[32px] border border-white/10 bg-white/5 p-6">
              <div className="text-xs uppercase tracking-[0.26em] text-white/40">Demo Videos</div>
              <div className="mt-6 space-y-5">
                {mirofishVideos.map((video) => (
                  <a
                    key={video.href}
                    href={video.href}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-[28px] border border-white/10 bg-black/35 p-4 transition hover:border-white/20 hover:bg-black/45"
                  >
                    <div className="overflow-hidden rounded-[20px] border border-white/10">
                      <Image src={video.image} alt={video.title} width={900} height={520} className="h-auto w-full object-cover" />
                    </div>
                    <div className="mt-4 text-xl font-semibold text-white">{video.title}</div>
                    <p className="mt-2 text-sm leading-6 text-white/60">{video.description}</p>
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-[32px] border border-white/10 bg-white/5 p-6">
              <div className="text-xs uppercase tracking-[0.26em] text-white/40">System Screenshots</div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {mirofishScreenshots.map((image, index) => (
                  <div key={image} className="overflow-hidden rounded-[24px] border border-white/10">
                    <Image
                      src={image}
                      alt={`MiroFish screenshot ${index + 1}`}
                      width={1200}
                      height={900}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          id="scenario-gallery"
          className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"
          data-testid="mirofish-scenario-gallery"
        >
          <div className="mb-8 flex items-center gap-3 text-sm uppercase tracking-[0.26em] text-white/40">
            <Sparkles className="h-4 w-4 text-amber-300" />
            Scenario Gallery
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {mirofishHomeScenarioCards.map((card) => (
              <div
                key={card.title}
                className="rounded-[28px] border border-white/10 bg-white/5 p-5"
                data-testid="mirofish-scenario-card"
              >
                <div className="text-xs uppercase tracking-[0.22em] text-sky-300">
                  {card.title}
                </div>
                <h2 className="mt-3 text-2xl font-semibold text-white">
                  {card.output}
                </h2>
                <p className="mt-3 text-sm leading-6 text-white/62">
                  {card.summary}
                </p>

                <div className="mt-5 rounded-[22px] border border-white/10 bg-black/25 p-4 text-sm">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/42">
                    Typical Input
                  </div>
                  <div className="mt-2 text-white/74">{card.input}</div>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent("scenario_card_click", {
                        scenario: card.title,
                        action: "use_template",
                      });
                      applyPromptTemplate(card.prompt, card.title);
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-white/90"
                  >
                    Use Template
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <Link
                    href={`/blog/${card.articleSlug}` as any}
                    onClick={() =>
                      trackEvent("scenario_card_click", {
                        scenario: card.title,
                        action: "open_guide",
                      })
                    }
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                  >
                    Open Guide
                    <BookOpenText className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section
          id="forecast-explorer"
          className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"
          data-testid="mirofish-forecast-explorer"
        >
          <div className="grid gap-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,1.05fr)]">
            <div className="rounded-[32px] border border-white/10 bg-white/5 p-6">
              <div className="text-xs uppercase tracking-[0.26em] text-white/40">
                Forecast Explorer
              </div>
              <h2 className="mt-4 text-3xl font-semibold text-white">
                See the output surface before you upload anything
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-7 text-white/63">
                The graph, simulation, and report views reveal what MiroFish
                actually produces, so the first visit becomes an inspection
                session instead of a blank decision.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                {mirofishForecastExplorerTabs.map((tab) => {
                  const active = tab.key === selectedExplorerTab;

                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => {
                        setSelectedExplorerTab(tab.key);
                        trackEvent("forecast_tab_switch", { tab: tab.key });
                      }}
                      className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.2em] transition ${
                        active
                          ? "border-sky-300/40 bg-sky-300/12 text-sky-100"
                          : "border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white/80"
                      }`}
                      data-testid="mirofish-forecast-tab"
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className="rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-6"
              data-testid="mirofish-forecast-panel"
            >
              <div className="text-xs uppercase tracking-[0.22em] text-white/45">
                {activeExplorerTab.eyebrow}
              </div>
              <h3 className="mt-4 text-2xl font-semibold text-white">
                {activeExplorerTab.title}
              </h3>
              <p className="mt-4 text-sm leading-7 text-white/62">
                {activeExplorerTab.description}
              </p>

              <div className="mt-6 rounded-[24px] border border-white/10 bg-black/35 p-5">
                <div className="space-y-3">
                  {activeExplorerTab.bullets.map((bullet) => (
                    <div key={bullet} className="flex gap-3 text-sm leading-6 text-white/70">
                      <div className="mt-1 h-2 w-2 rounded-full bg-sky-300" />
                      <div>{bullet}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() =>
                    applyPromptTemplate(
                      activeExplorerTab.prompt,
                      `forecast_explorer_${activeExplorerTab.key}`
                    )
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-white/90"
                >
                  Use This Prompt
                  <ArrowRight className="h-4 w-4" />
                </button>
                <Link
                  href="/blog/what-is-mirofish"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                >
                  Read Why It Works
                  <BookOpenText className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section
          id="prompt-library"
          className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"
          data-testid="mirofish-prompt-library"
        >
          <div className="mb-8 flex items-center gap-3 text-sm uppercase tracking-[0.26em] text-white/40">
            <Copy className="h-4 w-4 text-sky-300" />
            Prompt Recipe Library
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {mirofishPromptRecipes.map((recipe) => (
              <div
                key={recipe.title}
                className="rounded-[28px] border border-white/10 bg-white/5 p-5"
                data-testid="mirofish-prompt-recipe"
              >
                <h2 className="text-xl font-semibold text-white">{recipe.title}</h2>
                <p className="mt-3 text-sm leading-6 text-white/62">{recipe.summary}</p>
                <div className="mt-5 rounded-[22px] border border-white/10 bg-black/25 p-4 text-sm leading-7 text-white/72">
                  {recipe.prompt}
                </div>
                <button
                  type="button"
                  onClick={() => applyPromptTemplate(recipe.prompt, recipe.title)}
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                >
                  Use In Console
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section
          id="field-notes"
          className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"
          data-testid="mirofish-field-notes"
        >
          <div className="mb-8 flex items-center gap-3 text-sm uppercase tracking-[0.26em] text-white/40">
            <BookOpenText className="h-4 w-4 text-amber-300" />
            Field Notes
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {featuredPosts.map((post) => (
              <Link
                key={post.slug}
                href={`/blog/${post.slug}` as any}
                className="group rounded-[28px] border border-white/10 bg-white/5 p-5 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.07]"
                data-testid="mirofish-home-guide-card"
                onClick={() =>
                  trackEvent("homepage_blog_click", { slug: post.slug })
                }
              >
                <div className="text-xs uppercase tracking-[0.18em] text-white/42">
                  {formatDate(post.created_at)}
                </div>
                <h2 className="mt-4 text-xl font-semibold leading-snug text-white">
                  {post.title}
                </h2>
                <p className="mt-3 text-sm leading-6 text-white/58">
                  {post.description}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {(post.tags ?? [])
                    .filter((tag) => tag.toLowerCase() !== "mirofish")
                    .slice(0, 2)
                    .map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-white/54"
                      >
                        {formatTagLabel(tag)}
                      </span>
                    ))}
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/88">
                  Read note
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section
          id="trust-research"
          className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"
          data-testid="mirofish-trust-research"
        >
          <div className="mb-8 text-center">
            <div className="text-xs uppercase tracking-[0.26em] text-white/40">
              Trust & Research
            </div>
            <h2 className="mt-3 text-3xl font-semibold text-white">
              Stay longer where the judgment still matters
            </h2>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {mirofishTrustPillars.map((pillar) => (
              <div
                key={pillar.title}
                className="rounded-[28px] border border-white/10 bg-white/5 p-6"
              >
                <h3 className="text-xl font-semibold text-white">{pillar.title}</h3>
                <div className="mt-5 space-y-3">
                  {pillar.points.map((point) => (
                    <div
                      key={point}
                      className="flex gap-3 text-sm leading-6 text-white/62"
                    >
                      <div className="mt-1 h-2 w-2 rounded-full bg-amber-300" />
                      <div>{point}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="faq" className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.26em] text-white/40">FAQ</div>
            <h2 className="mt-3 text-3xl font-semibold text-white">Questions before you simulate</h2>
          </div>

          <div className="mt-10 divide-y divide-white/10 rounded-[32px] border border-white/10 bg-white/5">
            {mirofishFaq.map((item) => (
              <details key={item.question} className="group px-6 py-5">
                <summary className="cursor-pointer list-none text-lg font-medium text-white">
                  {item.question}
                </summary>
                <p className="mt-4 max-w-3xl text-sm leading-7 text-white/62">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <MiroFishSiteFooter />
    </div>
  );
}
